import { api } from '@/services/api'


import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import usersService, { User, UserQueryParams, CreateUserRequest, UpdateUserRequest } from '@/services/usersService'

export interface UsersState {
  users: User[]
  loading: boolean
  error: string | null
  total: number
  page: number
  limit: number
  totalPages: number
  searchTerm: string
  filters: {
    role?: string
    collegeId?: string
    isActive?: boolean
  }
  stats: {
    total: number
    active: number
    byRole: Record<string, number>
  }
}

const initialState: UsersState = {
  users: [],
  loading: false,
  error: null,
  total: 0,
  page: 1,
  limit: 10,
  totalPages: 0,
  searchTerm: '',
  filters: {},
  stats: {
    total: 0,
    active: 0,
    byRole: {}
  }
}

// Async thunks
export const fetchUsers = createAsyncThunk(
  'users/fetchUsers',
  async (params?: UserQueryParams) => {
    const response = await usersService.getUsers(params)
    return response
  }
)

export const fetchUserStats = createAsyncThunk(
  'users/fetchUserStats',
  async () => {
    const stats = await usersService.getUserStats()
    return stats
  }
)

export const fetchUserById = createAsyncThunk(
  'users/fetchUserById',
  async (id: string) => {
    const response = await usersService.getUserById(id)
    return response.data
  }
)

export const createUser = createAsyncThunk(
  'users/createUser',
  async (userData: CreateUserRequest) => {
    const response = await usersService.createUser(userData)
    return response.data
  }
)

export const updateUser = createAsyncThunk(
  'users/updateUser',
  async ({ id, userData }: { id: string; userData: UpdateUserRequest }) => {
    const response = await usersService.updateUser(id, userData)
    return response.data
  }
)

export const deleteUser = createAsyncThunk(
  'users/deleteUser',
  async (id: string) => {
    await usersService.deleteUser(id)
    return id
  }
)

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload
      state.page = 1 // Reset to first page when searching
    },
    setFilters: (state, action: PayloadAction<Partial<UsersState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload }
      state.page = 1 // Reset to first page when filtering
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload
    },
    setLimit: (state, action: PayloadAction<number>) => {
      state.limit = action.payload
      state.page = 1 // Reset to first page when changing limit
    },
    clearError: (state) => {
      state.error = null
    },
    clearUsers: (state) => {
      state.users = []
      state.total = 0
      state.totalPages = 0
    }
  },
  extraReducers: (builder) => {
    // Fetch users
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false
        state.users = action.payload.data
        state.total = action.payload.meta.total
        state.page = action.payload.meta.page
        state.limit = action.payload.meta.limit
        state.totalPages = action.payload.meta.totalPages
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch users'
      })

    // Fetch user stats
    builder
      .addCase(fetchUserStats.fulfilled, (state, action) => {
        state.stats = action.payload
      })

    // Create user
    builder
      .addCase(createUser.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.loading = false
        state.users.unshift(action.payload) // Add to beginning of list
        state.total += 1
      })
      .addCase(createUser.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to create user'
      })

    // Update user
    builder
      .addCase(updateUser.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.loading = false
        const index = state.users.findIndex(user => user.id === action.payload.id)
        if (index !== -1) {
          state.users[index] = action.payload
        }
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to update user'
      })

    // Delete user
    builder
      .addCase(deleteUser.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.loading = false
        state.users = state.users.filter(user => user.id !== action.payload)
        state.total -= 1
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to delete user'
      })
  }
})

export const {
  setSearchTerm,
  setFilters,
  setPage,
  setLimit,
  clearError,
  clearUsers
} = usersSlice.actions

export default usersSlice.reducer
